import { useEffect } from "react"

type SeoProps = {
  title: string
  description: string
  canonicalUrl?: string
  jsonLd?: Record<string, unknown>
  noindex?: boolean
}

const upsertMeta = (selector: string, attributes: Record<string, string>): HTMLMetaElement | null => {
  if (typeof document === "undefined") {
    return null
  }
  const existing = document.head.querySelector<HTMLMetaElement>(selector)
  if (existing) {
    Object.entries(attributes).forEach(([key, value]) => existing.setAttribute(key, value))
    return existing
  }
  const meta = document.createElement("meta")
  Object.entries(attributes).forEach(([key, value]) => meta.setAttribute(key, value))
  document.head.appendChild(meta)
  return meta
}

const upsertLink = (rel: string, href: string): HTMLLinkElement | null => {
  if (typeof document === "undefined") {
    return null
  }
  const existing = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (existing) {
    existing.setAttribute("href", href)
    return existing
  }
  const link = document.createElement("link")
  link.setAttribute("rel", rel)
  link.setAttribute("href", href)
  document.head.appendChild(link)
  return link
}

export const Seo = ({ title, description, canonicalUrl, jsonLd, noindex = false }: SeoProps): null => {
  useEffect(() => {
    if (typeof document === "undefined") {
      return
    }

    document.title = title

    upsertMeta('meta[name="description"]', { name: "description", content: description })
    upsertMeta('meta[name="robots"]', { name: "robots", content: noindex ? "noindex, nofollow" : "index, follow" })

    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title })
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description })

    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title })
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description })

    if (canonicalUrl) {
      upsertLink("canonical", canonicalUrl)
      upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl })
    }

    if (jsonLd) {
      const script = document.head.querySelector<HTMLScriptElement>('script[data-seo-jsonld="true"]') ?? document.createElement("script")
      script.type = "application/ld+json"
      script.setAttribute("data-seo-jsonld", "true")
      script.textContent = JSON.stringify(jsonLd)
      if (!script.parentElement) {
        document.head.appendChild(script)
      }
    }
  }, [canonicalUrl, description, jsonLd, noindex, title])

  return null
}
